const API = '/api'

function authHeaders() {
  const token = localStorage.getItem('agent_token')
  if (!token) {
    window.location.hash = '#/login'
    throw new Error('Nicht authentifiziert')
  }
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
}

async function handleResponse(res) {
  if (res.status === 401) {
    localStorage.removeItem('agent_token')
    window.location.hash = '#/login'
    throw new Error('Nicht authentifiziert')
  }
  // Nicht-JSON-Antworten (z.B. Nginx-Fehlerseiten bei 502/504) sauber abfangen
  const data = await res.json().catch(() => null)
  if (!res.ok) throw new Error(data?.error || `Serverfehler (HTTP ${res.status})`)
  if (data === null) throw new Error('Ungültige Antwort vom Server')
  return data
}

export const api = {
  // Generische Fetch-Methode fuer beliebige API-Calls
  fetch: (url, options = {}) => {
    const headers = { ...authHeaders(), ...(options.headers || {}) }
    return fetch(url, { ...options, headers }).then(handleResponse)
  },

  // Auth
  login: (username, password) =>
    fetch(`${API}/auth/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(username ? { username, password } : { password }) }).then(handleResponse),

  me: () =>
    fetch(`${API}/auth/me`, { headers: authHeaders() }).then(handleResponse),

  register: (data) =>
    fetch(`${API}/auth/register`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).then(handleResponse),

  registrationStatus: () =>
    fetch(`${API}/auth/registration-status`).then(handleResponse),

  changePassword: (data) =>
    fetch(`${API}/auth/change-password`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateProfile: (data) =>
    fetch(`${API}/auth/me`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),

  forgotPassword: (email) =>
    fetch(`${API}/auth/forgot-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) }).then(handleResponse),

  resetPassword: (token, newPassword) =>
    fetch(`${API}/auth/reset-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, newPassword }) }).then(handleResponse),

  getUsers: () =>
    fetch(`${API}/auth/users`, { headers: authHeaders() }).then(handleResponse),

  createUser: (data) =>
    fetch(`${API}/auth/users`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),

  updateUser: (id, data) =>
    fetch(`${API}/auth/users/${id}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),

  deleteUser: (id) =>
    fetch(`${API}/auth/users/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),

  listAppTokens: () =>
    fetch(`${API}/auth/app-tokens`, { headers: authHeaders() }).then(handleResponse),

  createAppToken: (label) =>
    fetch(`${API}/auth/app-tokens`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ label: label || null }) }).then(handleResponse),

  revokeAppToken: (tokenPrefix) =>
    fetch(`${API}/auth/app-tokens/${encodeURIComponent(tokenPrefix)}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),

  resetUserPassword: (id, newPassword) =>
    fetch(`${API}/auth/users/${id}/password`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ newPassword }) }).then(handleResponse),

  // Dashboard
  dashboard: () =>
    fetch(`${API}/dashboard`, { headers: authHeaders() }).then(handleResponse),

  // Emails
  emails: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return fetch(`${API}/emails?${qs}`, { headers: authHeaders() }).then(handleResponse)
  },
  email: (id) =>
    fetch(`${API}/emails/${id}`, { headers: authHeaders() }).then(handleResponse),
  updateEmail: (id, data) =>
    fetch(`${API}/emails/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  suggestReply: (id) =>
    fetch(`${API}/emails/${id}/suggest-reply`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  syncEmails: () =>
    fetch(`${API}/emails/sync`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  presence: () =>
    fetch(`${API}/emails/presence`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  syncStatus: () =>
    fetch(`${API}/emails/sync/status`, { headers: authHeaders() }).then(handleResponse),
  emailSettings: () =>
    fetch(`${API}/emails/settings`, { headers: authHeaders() }).then(handleResponse),
  saveEmailSettings: (data) =>
    fetch(`${API}/emails/settings`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  msOauthStatus: () =>
    fetch(`${API}/oauth/microsoft/status`, { headers: authHeaders() }).then(handleResponse),
  msOauthAuthorize: () =>
    fetch(`${API}/oauth/microsoft/authorize`, { headers: authHeaders() }).then(handleResponse),
  msOauthGraphAuthorize: () =>
    fetch(`${API}/oauth/microsoft/graph-authorize`, { headers: authHeaders() }).then(handleResponse),
  msOauthAdminConsentUrl: () =>
    fetch(`${API}/oauth/microsoft/admin-consent-url`, { headers: authHeaders() }).then(handleResponse),
  msOauthDisconnect: () =>
    fetch(`${API}/oauth/microsoft/disconnect`, { method: 'POST', headers: authHeaders() }).then(handleResponse),

  // Contacts
  contacts: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return fetch(`${API}/contacts?${qs}`, { headers: authHeaders() }).then(handleResponse)
  },
  contact: (id) =>
    fetch(`${API}/contacts/${id}`, { headers: authHeaders() }).then(handleResponse),
  allTags: () =>
    fetch(`${API}/contacts/tags/all`, { headers: authHeaders() }).then(handleResponse),
  addTag: (contactId, tag) =>
    fetch(`${API}/contacts/${contactId}/tags`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ tag }) }).then(handleResponse),
  removeTag: (contactId, tagId) =>
    fetch(`${API}/contacts/${contactId}/tags/${tagId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),

  // Projects
  projects: () =>
    fetch(`${API}/projects`, { headers: authHeaders() }).then(handleResponse),
  projectsSidebar: () =>
    fetch(`${API}/projects/sidebar`, { headers: authHeaders() }).then(handleResponse),
  project: (id) =>
    fetch(`${API}/projects/${id}`, { headers: authHeaders() }).then(handleResponse),
  projectModuleCounts: (id) =>
    fetch(`${API}/projects/${id}/module-counts`, { headers: authHeaders() }).then(handleResponse),
  createProject: (data) =>
    fetch(`${API}/projects`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateProject: (id, data) =>
    fetch(`${API}/projects/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  deleteProject: (id) =>
    fetch(`${API}/projects/${id}`, { method: 'DELETE', headers: authHeaders(), body: JSON.stringify({ confirm: true }) }).then(handleResponse),
  subscribeProject: (id) =>
    fetch(`${API}/projects/${id}/subscribe`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  unsubscribeProject: (id) =>
    fetch(`${API}/projects/${id}/subscribe`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  subscribedProjects: () =>
    fetch(`${API}/projects/subscribed`, { headers: authHeaders() }).then(handleResponse),
  deleteFact: (projectId, factId) =>
    fetch(`${API}/projects/${projectId}/facts/${factId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  updateFact: (projectId, factId, data) =>
    fetch(`${API}/projects/${projectId}/facts/${factId}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  addFact: (projectId, text) =>
    fetch(`${API}/projects/${projectId}/facts`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ text }) }).then(handleResponse),
  projectDashboard: (id) =>
    fetch(`${API}/projects/${id}/dashboard`, { headers: authHeaders() }).then(handleResponse),

  // Agent
  agentActions: (status) => {
    const qs = status ? `?status=${status}` : ''
    return fetch(`${API}/agent/actions${qs}`, { headers: authHeaders() }).then(handleResponse)
  },
  approveAction: (id) =>
    fetch(`${API}/agent/actions/${id}/approve`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  rejectAction: (id) =>
    fetch(`${API}/agent/actions/${id}/reject`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  resolveAction: (id) =>
    fetch(`${API}/agent/actions/${id}/resolve`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ result: { status: 'manuell_gesendet' } }) }).then(handleResponse),
  updateAction: (id, payload) =>
    fetch(`${API}/agent/actions/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ payload }) }).then(handleResponse),
  deleteAction: (id) =>
    fetch(`${API}/agent/actions/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  voiceToText: (audio, mimeType) =>
    fetch(`${API}/agent/voice`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ audio, mimeType }) }).then(handleResponse),
  contextThread: ({ emailId, bezugKey }) =>
    fetch(`${API}/agent/context-thread?${emailId ? `email_id=${encodeURIComponent(emailId)}` : `bezug_key=${encodeURIComponent(bezugKey)}`}`, { headers: authHeaders() }).then(handleResponse),
  checkText: (text) =>
    fetch(`${API}/agent/check-text`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ text }) }).then(handleResponse),
  chat: (message, project_id, currentPage) =>
    fetch(`${API}/agent/chat`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ message, project_id, currentPage }) }).then(handleResponse),
  chatStream: async (message, opts = {}) => {
    const { project_id, currentPage, conversationId, onToken, onTool, onInit, signal, images, context } = opts
    const body = { message, project_id, currentPage, conversationId }
    if (images?.length) body.images = images
    if (context) body.context = context
    const response = await fetch(`${API}/agent/chat/stream`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
      signal
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: `HTTP ${response.status}` }))
      throw new Error(err.error || `HTTP ${response.status}`)
    }
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let result = null
    // Stall-Watchdog: Der Server schickt alle 15s einen SSE-Heartbeat (': ping').
    // Kommen 60s lang KEINE Bytes, ist die Verbindung halbtot (Standby, Netz-
    // wechsel, Proxy-Kill) — reader.read() würde sonst für immer hängen und der
    // Spinner ewig drehen. Dann gezielt abbrechen; der Aufrufer lädt die Antwort
    // aus der DB nach (der Job rechnet serverseitig weiter).
    const STALL_MS = 60000
    while (true) {
      let stallTimer
      let readResult
      try {
        readResult = await Promise.race([
          reader.read(),
          new Promise((_, reject) => {
            stallTimer = setTimeout(() => reject(new Error('stream_stalled')), STALL_MS)
          })
        ])
      } catch (e) {
        if (e?.message === 'stream_stalled') reader.cancel().catch(() => {})
        throw e
      } finally {
        clearTimeout(stallTimer)
      }
      const { done, value } = readResult
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const parts = buffer.split('\n\n')
      buffer = parts.pop()
      for (const part of parts) {
        const line = part.trim()
        if (!line.startsWith('data: ')) continue
        try {
          const data = JSON.parse(line.slice(6))
          if (data.type === 'init') {
            if (data.conversationId) {
              result = { ...result, conversation_id: data.conversationId }
              onInit?.(data.conversationId)
            }
          } else if (data.type === 'text') {
            onToken?.(data.content)
          } else if (data.type === 'token') {
            onToken?.(data.content)
          } else if (data.type === 'tool_use') {
            onTool?.({ name: data.name, status: 'running' })
          } else if (data.type === 'tool') {
            onTool?.(data)
          } else if (data.type === 'result') {
            result = { ...result, ...data, type: 'result' }
          } else if (data.type === 'done') {
            result = { ...result, ...data }
          } else if (data.type === 'error') {
            throw new Error(data.message)
          }
        } catch (e) {
          if (e.message && !e.message.includes('JSON')) throw e
        }
      }
    }
    return result
  },
  chatHistory: (project_id) => {
    const qs = project_id ? `?project_id=${project_id}` : ''
    return fetch(`${API}/agent/chat${qs}`, { headers: authHeaders() }).then(handleResponse)
  },

  // Chat-Threading (Stufe 1)
  chatsList: (includeArchived = false) =>
    fetch(`${API}/chats${includeArchived ? '?archived=1' : ''}`, { headers: authHeaders() }).then(handleResponse),
  chatCreate: (title) =>
    fetch(`${API}/chats`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ title }) }).then(handleResponse),
  chatMessages: (conversationId, { signal } = {}) =>
    fetch(`${API}/chats/${conversationId}/messages`, { headers: authHeaders(), signal }).then(handleResponse),
  chatCancel: (conversationId) =>
    fetch(`${API}/agent/chat/${conversationId}/cancel`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  chatRemember: (conversationId) =>
    fetch(`${API}/agent/remember`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ conversationId }) }).then(handleResponse),
  chatActiveGet: () =>
    fetch(`${API}/chats/active`, { headers: authHeaders() }).then(handleResponse),
  chatActiveSet: (conversationId) =>
    fetch(`${API}/chats/active`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ conversation_id: conversationId }) }).then(handleResponse),
  chatsMerge: (targetId, sourceIds) =>
    fetch(`${API}/chats/merge`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ target_id: targetId, source_ids: sourceIds }) }).then(handleResponse),
  chatsMoveMessages: (messageIds, targetConversationId) =>
    fetch(`${API}/chats/move-messages`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ message_ids: messageIds, target_conversation_id: targetConversationId }) }).then(handleResponse),
  chatPatch: (conversationId, patch) =>
    fetch(`${API}/chats/${conversationId}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(patch) }).then(handleResponse),
  chatDelete: (conversationId, force = false) =>
    fetch(`${API}/chats/${conversationId}${force ? '?force=1' : ''}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  chatMemoryGet: (conversationId) =>
    fetch(`${API}/chats/${conversationId}/memory`, { headers: authHeaders() }).then(handleResponse),
  chatMemoryUpdate: (conversationId, content) =>
    fetch(`${API}/chats/${conversationId}/memory`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ content }) }).then(handleResponse),

  deleteChatMessage: (id) =>
    fetch(`${API}/agent/chat/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  uploadChatFile: (filename, data) =>
    fetch(`${API}/agent/chat/upload`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ filename, data }) }).then(handleResponse),

  // Calendar
  calendarEvents: (start, end) =>
    fetch(`${API}/calendar?start=${start}&end=${end}`, { headers: authHeaders() }).then(handleResponse),
  calendarByProject: (projectId, start, end) =>
    fetch(`${API}/calendar?start=${start}&end=${end}&project_id=${projectId}`, { headers: authHeaders() }).then(handleResponse),
  createAppointment: (data) =>
    fetch(`${API}/calendar`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateAppointment: (id, data) =>
    fetch(`${API}/calendar/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  deleteAppointment: (id) =>
    fetch(`${API}/calendar/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  inviteToAppointment: (id, recipients) =>
    fetch(`${API}/calendar/${id}/invite`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ recipients }) }).then(handleResponse),
  macCalendarSync: () =>
    fetch(`${API}/calendar/macos/sync`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  macCalendars: () =>
    fetch(`${API}/calendar/macos/calendars`, { headers: authHeaders() }).then(handleResponse),
  saveMacCalendarSettings: (data) =>
    fetch(`${API}/calendar/macos/settings`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  caldavSync: () =>
    fetch(`${API}/calendar/caldav/sync`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  caldavStatus: () =>
    fetch(`${API}/calendar/caldav/status`, { headers: authHeaders() }).then(handleResponse),
  outlookCalendars: () =>
    fetch(`${API}/calendar/outlook/calendars`, { headers: authHeaders() }).then(handleResponse),
  saveOutlookCalendarSettings: (selected) =>
    fetch(`${API}/calendar/outlook/settings`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ selected }) }).then(handleResponse),
  outlookCalendarSync: () =>
    fetch(`${API}/calendar/outlook/sync`, { method: 'POST', headers: authHeaders() }).then(handleResponse),

  // Kostenübersicht
  costOverviews: () =>
    fetch(`${API}/cost-overviews`, { headers: authHeaders() }).then(handleResponse),
  costOverview: (id) =>
    fetch(`${API}/cost-overviews/${id}`, { headers: authHeaders() }).then(handleResponse),
  createCostOverview: (data) =>
    fetch(`${API}/cost-overviews`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateCostOverview: (id, data) =>
    fetch(`${API}/cost-overviews/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateCostOverviewLines: (id, lines) =>
    fetch(`${API}/cost-overviews/${id}/lines`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ lines }) }).then(handleResponse),
  deleteCostOverview: (id) =>
    fetch(`${API}/cost-overviews/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  saveCostOverviewPayments: (id, lineId, payments) =>
    fetch(`${API}/cost-overviews/${id}/lines/${lineId}/payments`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ payments }) }).then(handleResponse),
  costOverviewSnapshots: (id) =>
    fetch(`${API}/cost-overviews/${id}/snapshots`, { headers: authHeaders() }).then(handleResponse),
  createCostOverviewSnapshot: (id, stand_datum, label) =>
    fetch(`${API}/cost-overviews/${id}/snapshot`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ stand_datum, label }) }).then(handleResponse),
  deleteCostOverviewSnapshot: (id, snapId) =>
    fetch(`${API}/cost-overviews/${id}/snapshot/${snapId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  downloadCostOverviewXlsx: async (id, filename) => {
    const res = await fetch(`${API}/cost-overviews/${id}/xlsx`, { headers: authHeaders() })
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Export fehlgeschlagen')
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename || 'Kostenübersicht.xlsx'
    document.body.appendChild(a); a.click(); a.remove()
    URL.revokeObjectURL(url)
  },
  costOverviewJournal: (id) =>
    fetch(`${API}/cost-overviews/${id}/journal`, { headers: authHeaders() }).then(handleResponse),
  addJournalPayment: (id, data) =>
    fetch(`${API}/cost-overviews/${id}/journal`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateJournalPayment: (id, paymentId, data) =>
    fetch(`${API}/cost-overviews/${id}/journal/${paymentId}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  deleteJournalPayment: (id, paymentId) =>
    fetch(`${API}/cost-overviews/${id}/journal/${paymentId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  downloadJournalPdf: async (id, filename) => {
    const res = await fetch(`${API}/cost-overviews/${id}/journal/pdf`, { headers: authHeaders() })
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'PDF-Export fehlgeschlagen')
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename || 'Zahlungsjournal.pdf'
    document.body.appendChild(a); a.click(); a.remove()
    URL.revokeObjectURL(url)
  },
  downloadKvExcelXlsx: async (id, filename) => {
    const res = await fetch(`${API}/kv-excel/${id}/xlsx`, { headers: authHeaders() })
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Export fehlgeschlagen')
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename || 'Kostenvoranschlag.xlsx'
    document.body.appendChild(a); a.click(); a.remove()
    URL.revokeObjectURL(url)
  },

  // Gantt
  ganttData: (view) =>
    fetch(`${API}/gantt?view=${view || 'month'}`, { headers: authHeaders() }).then(handleResponse),

  // Tasks
  tasks: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return fetch(`${API}/tasks?${qs}`, { headers: authHeaders() }).then(handleResponse)
  },
  createTask: (data) =>
    fetch(`${API}/tasks`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateTask: (id, data) =>
    fetch(`${API}/tasks/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  deleteTask: (id) =>
    fetch(`${API}/tasks/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  syncPendenzen: () =>
    fetch(`${API}/tasks/sync-pendenzen`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  createTaskBatch: (tasks, project_id) =>
    fetch(`${API}/tasks/batch`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ tasks, project_id }) }).then(handleResponse),

  emailDrafts: () =>
    fetch(`${API}/emails/drafts`, { headers: authHeaders() }).then(handleResponse),
  emailSuggest: (q) =>
    fetch(`${API}/emails/suggest?q=${encodeURIComponent(q)}`, { headers: authHeaders() }).then(handleResponse),

  // Emails - Send
  sendEmail: (data) =>
    fetch(`${API}/emails/send`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  uploadEmailAttachment: (filename, data) =>
    fetch(`${API}/emails/upload-attachment`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ filename, data }) }).then(handleResponse),
  replyEmail: (id, data) =>
    fetch(`${API}/emails/${id}/reply`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  batchEmails: (data) =>
    fetch(`${API}/emails/batch`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  deleteEmail: (id) =>
    fetch(`${API}/emails/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  batchDeleteEmails: (ids) =>
    fetch(`${API}/emails/batch-delete`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ ids }) }).then(handleResponse),

  // Agent - Briefing
  briefing: () =>
    fetch(`${API}/agent/briefing`, { method: 'POST', headers: authHeaders() }).then(handleResponse),

  // Agent - Brain (Gehirn)
  agentBrain: () =>
    fetch(`${API}/agent/brain`, { headers: authHeaders() }).then(handleResponse),
  updateBrainFact: (factId, data) =>
    fetch(`${API}/agent/facts/${factId}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  deleteBrainFact: (factId) =>
    fetch(`${API}/agent/facts/${factId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),

  // Setup
  setupStatus: () =>
    fetch(`${API}/setup/status`, { headers: authHeaders() }).then(handleResponse),
  setupComplete: () =>
    fetch(`${API}/setup/complete`, { method: 'POST', headers: authHeaders() }).then(handleResponse),

  // Settings
  getSettings: () =>
    fetch(`${API}/settings`, { headers: authHeaders() }).then(handleResponse),
  saveSettings: (data) =>
    fetch(`${API}/settings`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  uploadCompanyLogo: (data) =>
    fetch(`${API}/settings/company-logo`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ data }) }).then(handleResponse),
  deleteCompanyLogo: () =>
    fetch(`${API}/settings/company-logo`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  ollamaModels: () =>
    fetch(`${API}/settings/ollama/models`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  getMutationTemplate: () =>
    fetch(`${API}/settings/mutation-template`, { headers: authHeaders() }).then(handleResponse),
  uploadMutationTemplate: (data, filename) =>
    fetch(`${API}/settings/mutation-template`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ data, filename }) }).then(handleResponse),

  // Datenschutz
  getUserSettings: () =>
    fetch(`${API}/settings/user`, { headers: authHeaders() }).then(handleResponse),
  saveUserSettings: (data) =>
    fetch(`${API}/settings/user`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  piiStats: () =>
    fetch(`${API}/settings/pii-stats`, { headers: authHeaders() }).then(handleResponse),
  piiMappings: (category) =>
    fetch(`${API}/settings/pii-mappings${category ? '?category=' + category : ''}`, { headers: authHeaders() }).then(handleResponse),
  clearPiiMappings: () =>
    fetch(`${API}/settings/pii-mappings`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  dataOverview: () =>
    fetch(`${API}/settings/data-overview`, { headers: authHeaders() }).then(handleResponse),
  toolDefinitions: () =>
    fetch(`${API}/settings/tool-definitions`, { headers: authHeaders() }).then(handleResponse),
  deleteAllMyData: (confirmation) =>
    fetch(`${API}/settings/all-my-data`, { method: 'DELETE', headers: authHeaders(), body: JSON.stringify({ confirmation }) }).then(handleResponse),

  // Recordings
  recordingSessions: (project_id) =>
    fetch(`${API}/recordings/sessions?project_id=${project_id}`, { headers: authHeaders() }).then(handleResponse),
  createSession: (project_id) =>
    fetch(`${API}/recordings/sessions`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ project_id }) }).then(handleResponse),
  getSession: (id) =>
    fetch(`${API}/recordings/sessions/${id}`, { headers: authHeaders() }).then(handleResponse),
  deleteSession: (id) =>
    fetch(`${API}/recordings/sessions/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  uploadAudio: (sessionId, audio, duration) =>
    fetch(`${API}/recordings/sessions/${sessionId}/audio`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ audio, duration }) }).then(handleResponse),
  uploadPhoto: (sessionId, photo) =>
    fetch(`${API}/recordings/sessions/${sessionId}/photos`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ photo }) }).then(handleResponse),
  deleteRecording: (sessionId, recordingId) =>
    fetch(`${API}/recordings/sessions/${sessionId}/recordings/${recordingId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  deletePhoto: (sessionId, photoId) =>
    fetch(`${API}/recordings/sessions/${sessionId}/photos/${photoId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  transcribeSession: (sessionId) =>
    fetch(`${API}/recordings/sessions/${sessionId}/transcribe`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  getTemplates: () =>
    fetch(`${API}/recordings/templates`, { headers: authHeaders() }).then(handleResponse),
  generateProtocol: (sessionId, template) =>
    fetch(`${API}/recordings/sessions/${sessionId}/protocol`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ template }) }).then(handleResponse),
  audioUrl: (filename) => `${API}/recordings/audio/${filename}`,
  photoUrl: (filename) => `${API}/recordings/photos/${filename}`,

  // Schedules (Bauablaufplan)
  schedules: (project_id) =>
    fetch(`${API}/schedules?project_id=${project_id}`, { headers: authHeaders() }).then(handleResponse),
  createSchedule: (data) =>
    fetch(`${API}/schedules`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  importMppSchedule: (data) =>
    fetch(`${API}/schedules/import-mpp`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateSchedule: (id, data) =>
    fetch(`${API}/schedules/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  deleteSchedule: (id) =>
    fetch(`${API}/schedules/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  scheduleItems: (scheduleId) =>
    fetch(`${API}/schedules/${scheduleId}/items`, { headers: authHeaders() }).then(handleResponse),
  createScheduleItem: (scheduleId, data) =>
    fetch(`${API}/schedules/${scheduleId}/items`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateScheduleItem: (scheduleId, itemId, data) =>
    fetch(`${API}/schedules/${scheduleId}/items/${itemId}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  deleteScheduleItem: (scheduleId, itemId) =>
    fetch(`${API}/schedules/${scheduleId}/items/${itemId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  reorderScheduleItems: (scheduleId, items) =>
    fetch(`${API}/schedules/${scheduleId}/items/reorder`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ items }) }).then(handleResponse),
  schedulePdf: (scheduleId, format, orientation = 'landscape', rangeFrom = null, rangeTo = null, extra = {}) =>
    fetch(`${API}/schedules/${scheduleId}/pdf`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ format, orientation, rangeFrom, rangeTo, ...extra }) }).then(res => {
      if (!res.ok) throw new Error('PDF-Generierung fehlgeschlagen')
      return res.blob()
    }),
  schedulePdfPreview: (scheduleId, format, orientation = 'landscape', rangeFrom = null, rangeTo = null, extra = {}) =>
    fetch(`${API}/schedules/${scheduleId}/pdf-preview`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ format, orientation, rangeFrom, rangeTo, ...extra }) }).then(handleResponse),
  generateSchedule: (scheduleId, data) =>
    fetch(`${API}/schedules/${scheduleId}/generate`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  chatScheduleGeneration: (scheduleId, messages) =>
    fetch(`${API}/schedules/${scheduleId}/chat`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ messages }) }).then(handleResponse),
  generateScheduleFromChat: (scheduleId, messages) =>
    fetch(`${API}/schedules/${scheduleId}/generate-from-chat`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ messages }) }).then(handleResponse),
  createDependency: (scheduleId, fromItemId, toItemId) =>
    fetch(`${API}/schedules/${scheduleId}/dependencies`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ from_item_id: fromItemId, to_item_id: toItemId }) }).then(handleResponse),
  deleteDependency: (scheduleId, depId) =>
    fetch(`${API}/schedules/${scheduleId}/dependencies/${depId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),

  // Documents
  projectDocuments: (project_id) =>
    fetch(`${API}/documents?project_id=${project_id}`, { headers: authHeaders() }).then(handleResponse),
  uploadDocument: (project_id, filename, data) =>
    fetch(`${API}/documents`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ project_id, filename, data }) }).then(handleResponse),
  deleteDocument: (id) =>
    fetch(`${API}/documents/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  documentFileUrl: (filename) => `${API}/documents/file/${filename}`,

  // Ablage-Dokumente eines Projekts (aus der Ablage, projektgebunden)
  projectAblage: (projectId) =>
    fetch(`${API}/projects/${projectId}/ablage`, { headers: authHeaders() }).then(handleResponse),

  // Emails eines Projekts: fest zugeordnet + über Beteiligte (Adress-Match)
  projectEmails: (projectId) =>
    fetch(`${API}/projects/${projectId}/emails`, { headers: authHeaders() }).then(handleResponse),

  // Files (Dateimanager)
  projectFiles: (projectId, path = '') => {
    const qs = path ? `?path=${encodeURIComponent(path)}` : ''
    return fetch(`${API}/files/${projectId}${qs}`, { headers: authHeaders() }).then(handleResponse)
  },
  uploadFiles: (projectId, files, path = '') =>
    fetch(`${API}/files/${projectId}/upload`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ files, path }) }).then(handleResponse),
  createFolder: (projectId, name, path = '') =>
    fetch(`${API}/files/${projectId}/folder`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ name, path }) }).then(handleResponse),
  deleteFile: (projectId, path) =>
    fetch(`${API}/files/${projectId}?path=${encodeURIComponent(path)}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  moveFile: (projectId, from, to) =>
    fetch(`${API}/files/${projectId}/move`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ from, to }) }).then(handleResponse),
  fileDownloadUrl: (projectId, path) =>
    `${API}/files/${projectId}/download?path=${encodeURIComponent(path)}&token=${localStorage.getItem('agent_token')}`,
  generateFileStructure: (projectId, templateId) =>
    fetch(`${API}/files/${projectId}/structure`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ templateId }) }).then(handleResponse),
  fileTemplates: () =>
    fetch(`${API}/files/templates`, { headers: authHeaders() }).then(handleResponse),
  fileBridgeStatus: () =>
    fetch(`${API}/files/bridge/status`, { headers: authHeaders() }).then(handleResponse),
  generateDocument: (projectId, templateId, fields, targetPath = '') =>
    fetch(`${API}/files/${projectId}/generate-document`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ template_id: templateId, fields, target_path: targetPath }) }).then(handleResponse),
  fileVorlagen: () =>
    fetch(`${API}/files/vorlagen`, { headers: authHeaders() }).then(handleResponse),

  // Timesheet (Stundenliste)
  timesheetMonth: (year, month) =>
    fetch(`${API}/timesheet?year=${year}&month=${month}`, { headers: authHeaders() }).then(handleResponse),
  timesheetUpsert: (data) =>
    fetch(`${API}/timesheet/entry`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  timesheetDeleteEntry: (date, project_number) =>
    fetch(`${API}/timesheet/entry`, { method: 'DELETE', headers: authHeaders(), body: JSON.stringify({ date, project_number }) }).then(handleResponse),
  timesheetProjects: () =>
    fetch(`${API}/timesheet/projects`, { headers: authHeaders() }).then(handleResponse),
  timesheetAddProject: (data) =>
    fetch(`${API}/timesheet/projects`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  timesheetRemoveProject: (nr) =>
    fetch(`${API}/timesheet/projects/${encodeURIComponent(nr)}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  timesheetSollzeit: (data) =>
    fetch(`${API}/timesheet/sollzeit`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  timesheetImport: () =>
    fetch(`${API}/timesheet/import`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  timesheetExport: (year, month) =>
    fetch(`${API}/timesheet/export?year=${year}&month=${month}`, { method: 'POST', headers: authHeaders() }).then(handleResponse),

  // Fahrtenbuch
  fahrtenbuchMonth: (year, month) =>
    fetch(`${API}/fahrtenbuch?year=${year}&month=${month}`, { headers: authHeaders() }).then(handleResponse),
  fahrtenbuchUpsert: (data) =>
    fetch(`${API}/fahrtenbuch/entry`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  fahrtenbuchDelete: (id) =>
    fetch(`${API}/fahrtenbuch/entry/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),

  // Ablage (Ausgangskorb)
  ablageItems: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return fetch(`${API}/ablage?${qs}`, { headers: authHeaders() }).then(handleResponse)
  },
  ablageCount: () =>
    fetch(`${API}/ablage/count`, { headers: authHeaders() }).then(handleResponse),
  updateAblageItem: (id, data) =>
    fetch(`${API}/ablage/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  markAllAblageRead: () =>
    fetch(`${API}/ablage/read-all`, { method: 'PATCH', headers: authHeaders() }).then(handleResponse),
  deleteAblageItem: (id) =>
    fetch(`${API}/ablage/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  uploadAblageFile: (filename, data, folder = '/', project_id = null) =>
    fetch(`${API}/ablage/upload`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ filename, data, folder, project_id }) }).then(handleResponse),
  saveAttachmentToAblage: (emailId, attachmentIndex, filename) =>
    fetch(`${API}/ablage/from-email`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ email_id: emailId, attachment_index: attachmentIndex, filename }) }).then(handleResponse),
  createAblageFolder: (name, parent = '/', project_id = null, if_exists = 'error') =>
    fetch(`${API}/ablage/folder`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ name, parent, project_id, if_exists }) }).then(handleResponse),
  ablageMoveFolderContents: (oldPath, newPath) =>
    fetch(`${API}/ablage/move-folder`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ old_path: oldPath, new_path: newPath }) }).then(handleResponse),

  // KV (Kostenvoranschlag)
  kvList: (kategorie) =>
    fetch(`${API}/kv${kategorie ? `?kategorie=${kategorie}` : ''}`, { headers: authHeaders() }).then(handleResponse),
  kvListByProject: (projectId) =>
    fetch(`${API}/kv?project_id=${projectId}`, { headers: authHeaders() }).then(handleResponse),
  kvGet: (id) =>
    fetch(`${API}/kv/${id}`, { headers: authHeaders() }).then(handleResponse),
  kvUpload: (filename, data, project_id, titel, kategorie) =>
    fetch(`${API}/kv/upload`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ filename, data, project_id, titel, kategorie }) }).then(handleResponse),
  kvDelete: (id) =>
    fetch(`${API}/kv/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  kvSheets: (id) =>
    fetch(`${API}/kv/${id}/sheets`, { headers: authHeaders() }).then(handleResponse),
  kvSheet: (id, name) =>
    fetch(`${API}/kv/${id}/sheet/${encodeURIComponent(name)}`, { headers: authHeaders() }).then(handleResponse),
  kvWriteCells: (id, sheetName, cells) =>
    fetch(`${API}/kv/${id}/sheet/${encodeURIComponent(sheetName)}/cells`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ cells }) }).then(handleResponse),
  kvRecalculate: (id) =>
    fetch(`${API}/kv/${id}/recalculate`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  kvDownloadUrl: (id) =>
    `${API}/kv/${id}/download?token=${localStorage.getItem('agent_token')}`,
  kvTemplateUrl: (name) =>
    `${API}/kv/template/${name}?token=${localStorage.getItem('agent_token')}`,

  // Branding
  brandingGet: () =>
    fetch(`${API}/branding`, { headers: authHeaders() }).then(handleResponse),
  brandingSave: (data) =>
    fetch(`${API}/branding`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  brandingUploadLogo: (filename, data) =>
    fetch(`${API}/branding/logo`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ filename, data }) }).then(handleResponse),
  brandingDeleteLogo: () =>
    fetch(`${API}/branding/logo`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  brandingUploadFooterLogo: (filename, data) =>
    fetch(`${API}/branding/footer-logo`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ filename, data }) }).then(handleResponse),
  brandingDeleteFooterLogo: () =>
    fetch(`${API}/branding/footer-logo`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  brandingFooterLogoUrl: () =>
    `${API}/branding/footer-logo/file?token=${localStorage.getItem('agent_token')}`,
  brandingUploadFont: (filename, data) =>
    fetch(`${API}/branding/font`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ filename, data }) }).then(handleResponse),
  brandingDeleteFont: () =>
    fetch(`${API}/branding/font`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  brandingLogoUrl: () =>
    `${API}/branding/logo/file?token=${localStorage.getItem('agent_token')}`,
  brandingFontUrl: () =>
    `${API}/branding/font/file?token=${localStorage.getItem('agent_token')}`,
  brandingApplyToAll: () =>
    fetch(`${API}/branding/apply-to-all`, { method: 'POST', headers: authHeaders() }).then(handleResponse),

  // Sidebar-Module
  sidebarCatalog: () =>
    fetch(`${API}/sidebar/catalog`, { headers: authHeaders() }).then(handleResponse),
  sidebarModules: () =>
    fetch(`${API}/sidebar`, { headers: authHeaders() }).then(handleResponse),
  sidebarSave: (modules) =>
    fetch(`${API}/sidebar`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ modules }) }).then(handleResponse),

  // Post-its
  postits: () =>
    fetch(`${API}/postits`, { headers: authHeaders() }).then(handleResponse),
  createPostit: (data) =>
    fetch(`${API}/postits`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updatePostit: (id, data) =>
    fetch(`${API}/postits/${id}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  deletePostit: (id) =>
    fetch(`${API}/postits/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),

  // Mutations
  mutations: (projectId) =>
    fetch(`${API}/mutations?project_id=${projectId}`, { headers: authHeaders() }).then(handleResponse),
  mutationsAll: () =>
    fetch(`${API}/mutations/all`, { headers: authHeaders() }).then(handleResponse),
  mutation: (id) =>
    fetch(`${API}/mutations/${id}`, { headers: authHeaders() }).then(handleResponse),
  createMutation: (data) =>
    fetch(`${API}/mutations`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateMutation: (id, data) =>
    fetch(`${API}/mutations/${id}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateMutationStatus: (id, status, bewilligt_datum) =>
    fetch(`${API}/mutations/${id}/status`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ status, bewilligt_datum }) }).then(handleResponse),
  generateMutationXlsx: (id, ort, kuerzel) =>
    fetch(`${API}/mutations/${id}/xlsx`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ ort, kuerzel }) }).then(handleResponse),
  patchMutation: (id, data) =>
    fetch(`${API}/mutations/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  addMutationPosition: (mutId, data) =>
    fetch(`${API}/mutations/${mutId}/positions`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  patchMutationPosition: (mutId, posId, data) =>
    fetch(`${API}/mutations/${mutId}/positions/${posId}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  deleteMutationPosition: (mutId, posId) =>
    fetch(`${API}/mutations/${mutId}/positions/${posId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  reviseMutation: (id) =>
    fetch(`${API}/mutations/${id}/revise`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  deleteMutation: (id) =>
    fetch(`${API}/mutations/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  mutationDocUrl: (docId) =>
    `${API}/mutations/dokument/${docId}?token=${localStorage.getItem('agent_token')}`,

  // Cost Estimates (Kostenschätzungen)
  costEstimates: (projectNumber) => {
    const qs = projectNumber ? `?project_number=${encodeURIComponent(projectNumber)}` : ''
    return fetch(`${API}/cost-estimates${qs}`, { headers: authHeaders() }).then(handleResponse)
  },
  costEstimate: (id) =>
    fetch(`${API}/cost-estimates/${id}`, { headers: authHeaders() }).then(handleResponse),
  costEstimateSummary: (id) =>
    fetch(`${API}/cost-estimates/${id}/summary`, { headers: authHeaders() }).then(handleResponse),
  costEstimateUpdatePosition: (estimateId, posId, data) =>
    fetch(`${API}/cost-estimates/${estimateId}/position/${posId}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  costEstimateDeletePosition: (estimateId, posId) =>
    fetch(`${API}/cost-estimates/${estimateId}/position/${posId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  costEstimateDelete: (id) =>
    fetch(`${API}/cost-estimates/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  costEstimateExportUrl: (id) =>
    `${API}/cost-estimates/${id}/export?token=${localStorage.getItem('agent_token')}`,
  costEstimateUpdate: (id, data) =>
    fetch(`${API}/cost-estimates/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  costEstimateAddPosition: (estimateId, data) =>
    fetch(`${API}/cost-estimates/${estimateId}/positions`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),

  // Grobkostenschätzungen (Modul Kostenschätzung)
  grobksList: (projectNumber) => {
    const qs = projectNumber ? `?project_number=${encodeURIComponent(projectNumber)}` : ''
    return fetch(`${API}/grobks${qs}`, { headers: authHeaders() }).then(handleResponse)
  },
  grobksGet: (id) =>
    fetch(`${API}/grobks/${id}`, { headers: authHeaders() }).then(handleResponse),
  grobksCreate: (data) =>
    fetch(`${API}/grobks`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  grobksUpdate: (id, data) =>
    fetch(`${API}/grobks/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  grobksDelete: (id) =>
    fetch(`${API}/grobks/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  grobksAddRows: (id, sheet, rows) =>
    fetch(`${API}/grobks/${id}/rows/${sheet}`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ rows }) }).then(handleResponse),
  grobksUpdateRow: (id, sheet, rowId, data) =>
    fetch(`${API}/grobks/${id}/rows/${sheet}/${rowId}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  grobksDeleteRow: (id, sheet, rowId) =>
    fetch(`${API}/grobks/${id}/rows/${sheet}/${rowId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),

  // Ausschreibungen (Leistungsverzeichnisse pro Gewerk)
  ausschreibungen: (projectId) => {
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : ''
    return fetch(`${API}/ausschreibungen${qs}`, { headers: authHeaders() }).then(handleResponse)
  },
  ausschreibung: (id) =>
    fetch(`${API}/ausschreibungen/${id}`, { headers: authHeaders() }).then(handleResponse),
  ausschreibungCreate: (data) =>
    fetch(`${API}/ausschreibungen`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  ausschreibungUpdate: (id, data) =>
    fetch(`${API}/ausschreibungen/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  ausschreibungDelete: (id) =>
    fetch(`${API}/ausschreibungen/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  ausschreibungPositionen: (id, positionen, deleteIds) =>
    fetch(`${API}/ausschreibungen/${id}/positionen`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ positionen, delete_ids: deleteIds || [] }) }).then(handleResponse),
  aufmassNachweis: (id) =>
    fetch(`${API}/ausschreibungen/${id}/berechnungsdarstellung`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  aufmassPlaene: (projectId, ausschreibungId) =>
    fetch(`${API}/ausschreibungen/plaene?project_id=${projectId}${ausschreibungId ? `&ausschreibung_id=${ausschreibungId}` : ''}`,
      { headers: authHeaders() }).then(handleResponse),
  aufmassPlanAnalyse: (ablageId, seite = 0) =>
    fetch(`${API}/ausschreibungen/plan/${ablageId}/analyse?seite=${seite}`, { headers: authHeaders() }).then(handleResponse),
  aufmassZeilen: (posId) =>
    fetch(`${API}/ausschreibungen/positionen/${posId}/aufmass`, { headers: authHeaders() }).then(handleResponse),
  aufmassSpeichern: (posId, zeilen, deleteIds) =>
    fetch(`${API}/ausschreibungen/positionen/${posId}/aufmass`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ zeilen, delete_ids: deleteIds || [] }) }).then(handleResponse),
  ausschreibungExport: (id) =>
    fetch(`${API}/ausschreibungen/${id}/export`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  ausschreibungOffertenExport: (id) =>
    fetch(`${API}/ausschreibungen/${id}/offerten-export`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  ausschreibungOfferteAdd: (id, data) =>
    fetch(`${API}/ausschreibungen/${id}/offerten`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  ausschreibungOfferteUpdate: (offerteId, data) =>
    fetch(`${API}/ausschreibungen/offerten/${offerteId}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  ausschreibungOfferteDelete: (offerteId) =>
    fetch(`${API}/ausschreibungen/offerten/${offerteId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  ausschreibungErfahrungen: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString()
    return fetch(`${API}/ausschreibungen/erfahrungen${qs ? '?' + qs : ''}`, { headers: authHeaders() }).then(handleResponse)
  },
  ausschreibungErfahrungAdd: (data) =>
    fetch(`${API}/ausschreibungen/erfahrungen`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  ausschreibungErfahrungUpdate: (id, data) =>
    fetch(`${API}/ausschreibungen/erfahrungen/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  ausschreibungErfahrungDelete: (id) =>
    fetch(`${API}/ausschreibungen/erfahrungen/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  ausschreibungAuswertung: () =>
    fetch(`${API}/ausschreibungen/auswertung`, { headers: authHeaders() }).then(handleResponse),

  // NPK-Kataloge
  npkKataloge: () =>
    fetch(`${API}/npk`, { headers: authHeaders() }).then(handleResponse),
  npkPositionen: (katalogId, { q, chapter } = {}) => {
    const params = new URLSearchParams()
    if (q) params.set('q', q)
    if (chapter) params.set('chapter', chapter)
    return fetch(`${API}/npk/${katalogId}/positionen?${params}`, { headers: authHeaders() }).then(handleResponse)
  },
  npkImport: (ablageId) =>
    fetch(`${API}/npk/import`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ ablage_id: ablageId }) }).then(handleResponse),

  // Briefe
  letterPreviewUrl: () => `${API}/letters/preview`,
  letterGenerate: (data) =>
    fetch(`${API}/letters/generate`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  letterPreviewBlob: (data) =>
    fetch(`${API}/letters/preview`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) })
      .then(res => { if (!res.ok) throw new Error('Vorschau fehlgeschlagen'); return res.blob() }),

  // Notizen
  noteGenerate: (data) =>
    fetch(`${API}/notes/generate`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  notePreviewBlob: (data) =>
    fetch(`${API}/notes/preview`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) })
      .then(res => { if (!res.ok) throw new Error('Vorschau fehlgeschlagen'); return res.blob() }),

  // Rechnungsprüfung (Werkverträge)
  werkvertraege: (projectId) => {
    const qs = projectId ? `?project_id=${projectId}` : ''
    return fetch(`${API}/werkvertraege${qs}`, { headers: authHeaders() }).then(handleResponse)
  },
  werkvertrag: (id) =>
    fetch(`${API}/werkvertraege/${id}`, { headers: authHeaders() }).then(handleResponse),
  createWerkvertrag: (data) =>
    fetch(`${API}/werkvertraege`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateWerkvertrag: (id, data) =>
    fetch(`${API}/werkvertraege/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  deleteWerkvertrag: (id) =>
    fetch(`${API}/werkvertraege/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  setWerkvertragContext: (id, content) =>
    fetch(`${API}/werkvertraege/${id}/context`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ content }) }).then(handleResponse),
  addWerkvertragBeleg: (id, data) =>
    fetch(`${API}/werkvertraege/${id}/belege`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateWerkvertragBeleg: (belegId, data) =>
    fetch(`${API}/werkvertraege/belege/${belegId}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  deleteWerkvertragBeleg: (belegId) =>
    fetch(`${API}/werkvertraege/belege/${belegId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  addWerkvertragAbzug: (belegId, data) =>
    fetch(`${API}/werkvertraege/belege/${belegId}/abzuege`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  deleteWerkvertragAbzug: (abzugId) =>
    fetch(`${API}/werkvertraege/abzuege/${abzugId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  addWerkvertragPosition: (id, data) =>
    fetch(`${API}/werkvertraege/${id}/positionen`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateWerkvertragPosition: (posId, data) =>
    fetch(`${API}/werkvertraege/positionen/${posId}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  deleteWerkvertragPosition: (posId) =>
    fetch(`${API}/werkvertraege/positionen/${posId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  addRechnungsposition: (belegId, data) =>
    fetch(`${API}/werkvertraege/belege/${belegId}/positionen`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateRechnungsposition: (rpId, data) =>
    fetch(`${API}/werkvertraege/rechnungspositionen/${rpId}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  deleteRechnungsposition: (rpId) =>
    fetch(`${API}/werkvertraege/rechnungspositionen/${rpId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  positionAlsNachtrag: (rpId) =>
    fetch(`${API}/werkvertraege/rechnungspositionen/${rpId}/als-nachtrag`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
  addWerkvertragVerlauf: (id, text) =>
    fetch(`${API}/werkvertraege/${id}/verlauf`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ text }) }).then(handleResponse),
  deleteWerkvertragVerlauf: (verlaufId) =>
    fetch(`${API}/werkvertraege/verlauf/${verlaufId}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  stampPruefvermerk: (belegId) =>
    fetch(`${API}/werkvertraege/belege/${belegId}/pruefvermerk`, { method: 'POST', headers: authHeaders() }).then(handleResponse),

  // Vorgaenge
  vorgaenge: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return fetch(`${API}/vorgaenge${qs ? '?' + qs : ''}`, { headers: authHeaders() }).then(handleResponse)
  },
  vorgang: (id) =>
    fetch(`${API}/vorgaenge/${id}`, { headers: authHeaders() }).then(handleResponse),
  createVorgang: (data) =>
    fetch(`${API}/vorgaenge`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  updateVorgang: (id, data) =>
    fetch(`${API}/vorgaenge/${id}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  addVorgangEvent: (id, data) =>
    fetch(`${API}/vorgaenge/${id}/events`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(handleResponse),
  deleteVorgang: (id) =>
    fetch(`${API}/vorgaenge/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handleResponse),
  refreshVorgaenge: () =>
    fetch(`${API}/vorgaenge/refresh`, { method: 'POST', headers: authHeaders() }).then(handleResponse),
}
